import {
  cast,
  destroy,
  getParent,
  getParentOfType,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree'
import { Question } from './Question'
import { Factoid } from './Factoid'

export const ChoicePair = types
  .model('ChoicePair')
  .props({
    left: types.string,
    right: types.string,
    touched: types.maybe(types.boolean),
  })
  .actions((self) => ({
    setTouched(touched) {
      self.touched = touched
    },
    setLeft(left) {
      self.left = left
      self.touched = true
    },
    setRight(right) {
      self.right = right
      self.touched = true
    },
    reset() {
      self.left = ''
      self.right = ''
      self.touched = false
    },
    delete() {
      const parent = getParent(self, 2) as Instance<typeof QuestionChoice>
      if (parent) {
        parent.removePair(self)
      }
    },
  }))
  .views((self) => ({
    get formErrors() {
      const errors: any = {}
      if (!self.left) {
        errors.left = {
          type: 'error',
          message: 'Choice text cannot be blank.',
        }
      }

      if (!self.right) {
        errors.right = {
          type: 'error',
          message: 'Matching text cannot be blank.',
        }
      }

      if (self.left.length > 80) {
        errors.left = {
          type: 'error',
          message: 'Choice text must be less than 80 characters.',
        }
      }
      if (self.right.length > 80) {
        errors.right = {
          type: 'error',
          message: 'Matching text must be less than 80 characters.',
        }
      }

      try {
        const question = getParentOfType(self, Question) as any
        if (question?.question_type === 'Matching') {
          const norm = (v: string) => v?.trim().toLowerCase()
          const myLeft = norm(self.left)
          const myRight = norm(self.right)

          const allValues: string[] = []
          question.choices?.forEach((choice: any) => {
            choice.pairs?.forEach((pair: any) => {
              const l = norm(pair.left)
              const r = norm(pair.right)
              if (l) allValues.push(l)
              if (r) allValues.push(r)
            })
          })

          if (myLeft && !errors.left && allValues.filter((v) => v === myLeft).length > 1) {
            errors.left = {
              type: 'error',
              message: 'Choice pairs for a matching question cannot contain a duplicate value.',
            }
          }
          if (myRight && !errors.right && allValues.filter((v) => v === myRight).length > 1) {
            errors.right = {
              type: 'error',
              message: 'Choice pairs for a matching question cannot contain a duplicate value.',
            }
          }
        }
      } catch {
        // pair is not attached to a Question yet
      }

      return errors
    },
    get allErrors() {
      return [...Object.values(this.formErrors)]
    },
  }))

export const Ordering = types
  .model('Ordering')
  .props({
    index: types.maybe(types.number),
    text: types.string,
    touched: types.maybe(types.boolean),
  })
  .actions((self) => ({
    setTouched(touched) {
      self.touched = touched
    },
    reset() {
      self.text = ''
      self.index = undefined
    },
    setText(text: string) {
      self.text = text
    },
    delete() {
      const parent = getParent(self, 2) as Instance<typeof QuestionChoice>
      if (parent) {
        parent.removeOrdering(self)
      }
    },
  }))
  .views((self) => ({
    get formErrors() {
      const errors: any = {}
      if (!self.text.trim().length) {
        errors.text = {
          type: 'error',
          message: 'Choice text cannot be blank.',
        }
      }
      return errors
    },
  }))

export const QuestionChoice = types
  .model('QuestionChoice')
  .props({
    choice_id: types.maybeNull(types.number),
    text: types.maybeNull(types.string),
    correct: types.boolean,
    errors: types.maybeNull(types.array(types.string)),
    errorsCorrected: types.maybeNull(types.array(types.string)),
    warnings: types.maybeNull(types.array(types.string)),
    acceptedWarnings: types.maybeNull(types.array(types.string)),
    order: types.maybeNull(types.number),
    touched: types.maybe(types.boolean),
    generating: types.maybe(types.boolean),
    loading: types.maybe(types.boolean),
    factoid: types.maybeNull(Factoid),
    pairs: types.maybe(types.array(ChoicePair)),
    ordering: types.maybe(types.array(Ordering)),
  })
  .actions((self) => ({
    set<K extends keyof SnapshotIn<typeof self>, T extends SnapshotIn<typeof self>>(
      key: K,
      value: T[K],
    ) {
      self[key] = cast(value)
    },
    setPairs(pairs) {
      self.pairs?.replace(pairs)
    },
    setFactoid(factoid: typeof self.factoid) {
      self.factoid = factoid
    },
    setOrdering(ordering) {
      self.ordering?.replace(ordering)
    },
    removePair(pair) {
      destroy(pair)
    },
    removeOrdering(ordering) {
      destroy(ordering)
    },
    reset() {
      self.text = ''
      self.touched = false
      self.pairs?.map((p) => p.reset())
      self.correct = false
    },
    setTouched(touched: boolean) {
      const question = getParentOfType(self, Question) as any
      if (question && touched) {
        question.setTouched(touched)
        question.setApproved(false)
      }
      self.touched = touched
    },
    setText(text: string | null) {
      self.text = text
    },
  }))
  .views((self) => ({
    get formWarnings() {
      const warnings: any = {}

      if (self.text && self.text.length > 100) {
        warnings.text = {
          type: 'warning',
          message:
            'Choice text is over the recommended character limit. It may not display correctly on certain devices.',
        }
      }

      if (self.factoid && self.factoid?.text && self.factoid.text.length > 100) {
        warnings.feedback = {
          type: 'warning',
          message: `Choice feedback is longer than 100 characters and may not display correctly on some devices.`,
        }
      }
      return warnings
    },
    get formErrors() {
      const errors: any = {}
      const question = getParent(self, 2) as any

      if (
        !self.text?.trim().length &&
        question.question_type !== 'Card' &&
        question.question_type !== 'Matching' &&
        question.question_type !== 'Ordering'
      ) {
        errors.text = {
          type: 'error',
          message: 'Choice text cannot be blank.',
        }
      }

      return errors
    },
    get pairErrors() {
      const err = [] as any
      if (self.pairs && self.pairs?.length) {
        self.pairs.map((pair) => {
          if (pair.formErrors) {
            err.push(...Object.values(pair.formErrors))
          }
        })
      }
      return err
    },
    get orderedErrors() {
      const err = [] as any
      if (self.ordering && self.ordering?.length) {
        self.ordering.map((ordering) => {
          if (ordering.formErrors) {
            err.push(...Object.values(ordering.formErrors))
          }
        })
      }
      return err
    },
    get allErrors() {
      return [...Object.values(this.formErrors), ...this.pairErrors, ...this.orderedErrors]
    },
  }))
